import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

const CART_KEY = "pe_cart";

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY) || "[]"); } catch { return []; }
  });
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    // Skip /auth/me if we're mid-OAuth callback; AuthCallback handles session exchange first.
    if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
      return;
    }
    const t = localStorage.getItem("pe_token");
    if (t) {
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
        localStorage.removeItem("pe_token");
      });
    }
  }, []);

  const loginWithToken = (token, u) => {
    localStorage.setItem("pe_token", token);
    setUser(u);
  };

  const logout = () => {
    localStorage.removeItem("pe_token");
    setUser(null);
  };

  const addToCart = (product, variant, qty = 1) => {
    setCart((prev) => {
      const key = `${product.id}:${variant.id}`;
      const idx = prev.findIndex((c) => `${c.product_id}:${c.variant_id}` === key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + qty };
        return copy;
      }
      return [...prev, {
        product_id: product.id,
        variant_id: variant.id,
        title: product.title,
        variant_name: variant.name,
        price_inr: variant.price_inr,
        image_url: product.image_url,
        qty,
      }];
    });
  };

  const updateQty = (idx, qty) => {
    setCart((prev) => prev.map((c, i) => (i === idx ? { ...c, qty: Math.max(1, qty) } : c)));
  };

  const removeItem = (idx) => setCart((prev) => prev.filter((_, i) => i !== idx));
  const clearCart = () => setCart([]);

  const subtotal = cart.reduce((s, c) => s + c.price_inr * c.qty, 0);

  const value = useMemo(() => ({
    user, setUser, loginWithToken, logout,
    cart, addToCart, updateQty, removeItem, clearCart, subtotal,
    authOpen, setAuthOpen,
  }), [user, cart, subtotal, authOpen]);

  return (
    <AppCtx.Provider value={value}>
      {children}
    </AppCtx.Provider>
  );
}
